import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { TableCheckboxData } from '@/ui/Table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { showSelectionLimitReachedToast } from '@/ui/selection-limit-toast';
import {
    reconcilePickedIdsWithVisible,
    removePagedIds,
    selectPagedIdsWithLimit,
    toggleItemSelection,
} from '@/components/group-tables/selection-state';

type SelectableGroupItem = {
    id: string;
};

type UseGroupTableSelectionArgs<TItem extends SelectableGroupItem> = {
    pickedIds: string[];
    setPickedIds?: (value: string[]) => void;
    maxSelectedItems: number | null;
    visibleItems: TItem[];
    pagedItems: TItem[];
    syncAddIds?: (ids: string[]) => Promise<unknown>;
    syncRemoveIds?: (ids: string[]) => Promise<unknown>;
    onSyncError?: (error: unknown) => void;
};

const groupTableSelectionLogger = createClientLogger({
    scope: 'group-table-selection',
});

const logSyncError = (error: unknown): void => {
    logClientError({
        logger: groupTableSelectionLogger,
        message: '[client.group-management.selection-sync] Failed to sync group table selection state.',
        error,
    });
};

export const useGroupTableSelection = <TItem extends SelectableGroupItem>({
    pickedIds,
    setPickedIds,
    maxSelectedItems,
    visibleItems,
    pagedItems,
    syncAddIds,
    syncRemoveIds,
    onSyncError = logSyncError,
}: UseGroupTableSelectionArgs<TItem>) => {
    const pickedIdsRef = useRef(pickedIds);
    const pickedIdsSet = useMemo(() => new Set(pickedIds), [pickedIds]);

    useEffect(() => {
        pickedIdsRef.current = pickedIds;
    }, [pickedIds]);

    const syncAddedIds = useCallback(
        (addIds: string[]) => {
            if (!syncAddIds || !addIds.length) {
                return;
            }

            void syncAddIds(addIds).catch(onSyncError);
        },
        [onSyncError, syncAddIds],
    );

    const syncRemovedIds = useCallback(
        (removeIds: string[]) => {
            if (!syncRemoveIds || !removeIds.length) {
                return;
            }

            void syncRemoveIds(removeIds).catch(onSyncError);
        },
        [onSyncError, syncRemoveIds],
    );

    useEffect(() => {
        if (!setPickedIds || !pickedIds.length) {
            return;
        }

        const { nextPickedIds, removedIds } = reconcilePickedIdsWithVisible({
            pickedIds,
            visibleIds: visibleItems.map((item) => item.id),
        });
        if (!removedIds.length) {
            return;
        }

        syncRemovedIds(removedIds);
        pickedIdsRef.current = nextPickedIds;
        setPickedIds(nextPickedIds);
    }, [pickedIds, setPickedIds, syncRemovedIds, visibleItems]);

    const onHeaderCheckboxChange = useCallback(
        (checked: boolean) => {
            if (!setPickedIds) {
                return;
            }
            const currentPickedIds = pickedIdsRef.current;

            if (checked) {
                const { addIds, nextPickedIds, isSelectionLimitReached } = selectPagedIdsWithLimit({
                    pickedIds: currentPickedIds,
                    pagedIds: pagedItems.map((item) => item.id),
                    maxSelectedItems,
                });

                syncAddedIds(addIds);

                if (isSelectionLimitReached && maxSelectedItems !== null) {
                    showSelectionLimitReachedToast({ maxSelectedItems, scope: 'groups' });
                }

                pickedIdsRef.current = nextPickedIds;
                setPickedIds(nextPickedIds);
                return;
            }

            const { removeIds, nextPickedIds } = removePagedIds({
                pickedIds: currentPickedIds,
                pagedIds: pagedItems.map((item) => item.id),
            });

            syncRemovedIds(removeIds);
            pickedIdsRef.current = nextPickedIds;
            setPickedIds(nextPickedIds);
        },
        [maxSelectedItems, pagedItems, setPickedIds, syncAddedIds, syncRemovedIds],
    );

    const onCheckboxChange = useCallback(
        (checked: boolean, item: TItem) => {
            if (!setPickedIds) {
                return;
            }
            const currentPickedIds = pickedIdsRef.current;

            const selectionResult = toggleItemSelection({
                checked,
                itemId: item.id,
                pickedIds: currentPickedIds,
                maxSelectedItems,
            });

            if (selectionResult.isSelectionLimitReached && maxSelectedItems !== null) {
                showSelectionLimitReachedToast({ maxSelectedItems, scope: 'groups' });
                return;
            }

            if (!selectionResult.changed) {
                return;
            }

            syncAddedIds(selectionResult.addIds);
            syncRemovedIds(selectionResult.removeIds);

            pickedIdsRef.current = selectionResult.nextPickedIds;
            setPickedIds(selectionResult.nextPickedIds);
        },
        [maxSelectedItems, setPickedIds, syncAddedIds, syncRemovedIds],
    );

    const isHeaderChecked = useMemo(() => {
        return !!pagedItems.length && pagedItems.every((item) => pickedIdsSet.has(item.id));
    }, [pagedItems, pickedIdsSet]);

    const checkboxData = useMemo<TableCheckboxData[]>(() => {
        const isSelectionLimitReached = maxSelectedItems !== null && pickedIds.length >= maxSelectedItems;

        return pagedItems.map((item) => ({
            checked: pickedIdsSet.has(item.id),
            disabled: isSelectionLimitReached && !pickedIdsSet.has(item.id),
        }));
    }, [maxSelectedItems, pagedItems, pickedIds.length, pickedIdsSet]);

    return {
        onHeaderCheckboxChange,
        onCheckboxChange,
        isHeaderChecked,
        checkboxData,
    };
};
